/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import './table-toolbar.scss';
import { Search, X } from 'react-feather';
import DropdownMultiSelect from '../../dropdown-multi-select';
import useTranslation from '../../../../utils/translation-utils';
import Button, { ButtonSizes, ButtonTypes } from '../../button';
import { FilterTypes } from './constants';
import DropdownSingleSelect from '../../dropdown-single-select';
import DateRangePicker, { DatePickerPositions } from '../../date-range-picker';
import useMobileView from '../../../enhancers/custom-hook/use-mobile-view';
import SearchInput from '../../search-input';
import { getProcessedTableToolbarFiltersList } from './utils/actions';
import MobileFilter from '../../mobile-filter';
import TooltipButton from './views/tooltip-button/tooltip-button';
import { getRenderIcon } from '../../../../utils/general-utils';

const TableToolbar = ({
  search, columns, filters, bulkActions, buttons,
}) => {
  const [getTranslation] = useTranslation();
  const [searchText, setSearchText] = useState(search ? search.value || '' : '');
  const [mobileSearchBarExpanded, setMobileSearchBarExpanded] = useState(false);
  const isMobileView = useMobileView();
  const { onApply } = search || {};
  const mobileSearchRefCallback = useCallback((input) => input && input.focus(), []);
  const mobileSearchInputApplyCallback = useCallback(() => onApply(searchText), [searchText]);
  const mobileSearchInputChangeCallback = useCallback((e) => setSearchText(e.target.value), []);
  const mobileSearchButtonCallback = useCallback(() => setMobileSearchBarExpanded(true), []);
  const mobileSearchBarCallback = useCallback(() => {
    setSearchText('');
    onApply('');
    setMobileSearchBarExpanded(false);
  }, []);

  useEffect(() => {
    setSearchText(search ? search.value : '');
  }, [search ? search.value : null]);

  const colListWithSelection = useMemo(() => {
    if (columns) {
      const { list = [], selectedIds = [] } = columns;

      return list.filter(({ optional }) => optional).map((col) => ({
        ...col,
        selected: selectedIds.includes(col.id),
      }));
    }
    return [];
  }, [columns ? columns.list : null, columns ? columns.selectedIds : null]);

  const getSearchInput = () => {
    if (search) {
      const {
        label, placeholder,
      } = search;

      return (
        <SearchInput
          label={label}
          value={searchText}
          placeholder={placeholder}
          className="toolbar-search-input"
          onApply={mobileSearchInputApplyCallback}
          onChange={mobileSearchInputChangeCallback}
          ref={isMobileView ? mobileSearchRefCallback : null}
        />
      );
    }
    return null;
  };

  const getMobileSearchBar = () => {
    if (!search) return null;
    if (mobileSearchBarExpanded) {
      return (
        <div className="ttm-search-bar">
          <Button
            borderLess
            renderIcon={getRenderIcon(X)}
            type={ButtonTypes.SECONDARY}
            className="ttm-search-close-button"
            onClick={mobileSearchBarCallback}
          />
          {getSearchInput()}
        </div>
      );
    }
    return (
      <Button
        borderLess
        renderIcon={getRenderIcon(Search)}
        type={ButtonTypes.SECONDARY}
        className="ttm-search-button"
        onClick={mobileSearchButtonCallback}
      />
    );
  };

  const getColumnSelector = () => {
    if (columns) {
      const { onChange, onSelectionClear, placeholder } = columns;

      return (
        <DropdownMultiSelect
          label={getTranslation('COLUMNS')}
          placeholder={placeholder}
          items={colListWithSelection}
          onSelect={onChange}
          showLabelInline
          onSelectionClear={onSelectionClear}
        />
      );
    }
    return null;
  };

  const fillSelectedPropInItems = (items, selectedIds = []) => {
    if (selectedIds.length) {
      return items.map((item) => ({ ...item, selected: selectedIds.includes(item.id) }));
    }
    return items;
  };

  const handleMultiSelectFilterChange = (filterId, itemId) => {
    const { valuesMap, onChange } = filters;
    const modifiedValuesMap = { ...valuesMap };

    if (onChange && typeof onChange === 'function') {
      const selectedIds = valuesMap[filterId] || [];
      if (selectedIds.includes(itemId)) {
        modifiedValuesMap[filterId] = selectedIds.filter((val) => val !== itemId);
      } else {
        modifiedValuesMap[filterId] = [...selectedIds, itemId];
      }

      onChange(modifiedValuesMap);
    }
  };

  const handleSelectionClear = (id) => {
    const { valuesMap, onChange } = filters;
    const filterData = { ...valuesMap };
    filterData[id] = [];
    onChange(filterData);
  };

  const handleSingleSelectFilterChange = (filterId, itemId, allowDeselection = true) => {
    const { valuesMap, onChange } = filters;
    const modifiedValuesMap = { ...valuesMap };

    if (onChange && typeof onChange === 'function') {
      const selectedIds = valuesMap[filterId] || [];

      if (selectedIds.includes(itemId)) {
        if (allowDeselection) {
          modifiedValuesMap[filterId] = [];
        } else {
          return;
        }
      } else {
        modifiedValuesMap[filterId] = [itemId];
      }

      onChange(modifiedValuesMap);
    }
  };

  const handleDateRangeChange = (filterId, startDate, endDate) => {
    const { valuesMap, onChange } = filters;
    const modifiedValuesMap = { ...valuesMap };

    if (onChange && typeof onChange === 'function') {
      modifiedValuesMap[filterId] = {
        startDate,
        endDate,
      };
      onChange(modifiedValuesMap);
    }
  };

  const getMultiFilter = ({
    id, label, items, placeholder, showLabelInline, withSearch,
  }, valuesMap, index) => (
    <DropdownMultiSelect
      key={id}
      label={label}
      withSearch={withSearch}
      className={index ? '' : 'table-tb-mar-left-auto'}
      placeholder={placeholder}
      items={fillSelectedPropInItems(items, valuesMap[id])}
      showLabelInline={showLabelInline}
      onSelect={(itemId) => { handleMultiSelectFilterChange(id, itemId); }}
      onSelectionClear={() => handleSelectionClear(id)}
    />
  );

  const getSingleFilter = ({
    id, items, allowDeselection, ...props
  }, valuesMap, index) => (
    <DropdownSingleSelect
      key={id}
      {...props}
      className={index ? '' : 'table-tb-mar-left-auto'}
      items={fillSelectedPropInItems(items, valuesMap[id])}
      onSelect={(itemId) => { handleSingleSelectFilterChange(id, itemId, allowDeselection); }}
    />
  );

  const getDateRangeFilter = ({
    id, label, pickerPosition, ...rest
  }, valuesMap, index) => (
    <DateRangePicker
      key={id}
      {...rest}
      startDate={valuesMap[id] ? valuesMap[id].startDate : ''}
      endDate={valuesMap[id] ? valuesMap[id].endDate : ''}
      onChange={(stDt, enDt) => handleDateRangeChange(id, stDt, enDt)}
      className={index ? '' : 'table-tb-mar-left-auto'}
      position={pickerPosition || DatePickerPositions.CENTER}
    />
  );

  const getMobileFilter = () => {
    if (!mobileSearchBarExpanded && filters && filters.list && filters.list.length > 0) {
      const processedFilters = {
        ...filters,
        list: getProcessedTableToolbarFiltersList(filters.list),
      };

      return <MobileFilter filters={processedFilters} />;
    }
    return null;
  };

  const getFilters = () => {
    if (filters) {
      const { list, valuesMap } = filters;
      const processedFilterList = getProcessedTableToolbarFiltersList(list);

      return processedFilterList?.map(({
        type, ...listItem
      }, index) => {
        switch (type) {
          case FilterTypes.MULTI_FILTER:
            return getMultiFilter(listItem, valuesMap, index);
          case FilterTypes.SINGLE_FILTER:
            return getSingleFilter(listItem, valuesMap, index);
          case FilterTypes.DATE_RANGE:
            return getDateRangeFilter(listItem, valuesMap, index);
          default:
            return null;
        }
      });
    }
    return null;
  };

  const getBulkActions = () => {
    if (bulkActions && bulkActions.selectedItemCount) {
      const { selectedItemCount, onClick, buttons: bulkActionButtons } = bulkActions;
      return (
        <div className="table-toolbar-bulk-actions">
          <div className="ttba-text">
            {`${selectedItemCount} ${getTranslation('SELECTED_LOWER')}`}
          </div>
          {bulkActionButtons.map(({
            id, disabled, type, label, renderIcon, className,
          }) => (
            <Button
              disabled={disabled}
              size={ButtonSizes.SMALL}
              type={type}
              onClick={() => onClick(id)}
              label={label}
              renderIcon={renderIcon}
              key={id}
              id={id}
              className={className}
            />
          ))}
        </div>
      );
    }
    return null;
  };

  const getSearchFilter = () => {
    if (bulkActions && bulkActions.selectedItemCount) {
      return null;
    }

    return (
      <div className={clsx('table-toolbar-search-filter', { expanded: mobileSearchBarExpanded })}>
        {isMobileView ? getMobileSearchBar() : getSearchInput()}
        {getColumnSelector()}
        {isMobileView ? getMobileFilter() : getFilters()}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {buttons.map((button) => <TooltipButton key={button.id} {...button} />)}
      </div>
    );
  };

  return (
    <div className="table-toolbar-cont">
      {getSearchFilter()}
      {getBulkActions()}
    </div>
  );
};

TableToolbar.propTypes = {
  search: PropTypes.shape({
    label: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.string,
    onApply: PropTypes.func,
  }),
  columns: PropTypes.shape({
    list: PropTypes.arrayOf(PropTypes.shape),
    selectedIds: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
  }),
  filters: PropTypes.shape({
    list: PropTypes.arrayOf(PropTypes.shape),
    valuesMap: PropTypes.shape(),
    onChange: PropTypes.func,
  }),
  bulkActions: PropTypes.shape({
    selectedItemCount: PropTypes.number,
    buttons: PropTypes.arrayOf(PropTypes.shape),
    onClick: PropTypes.func,
  }),
  buttons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    type: PropTypes.oneOf(Object.values(ButtonTypes)),
  })),
};

TableToolbar.defaultProps = {
  search: null,
  columns: null,
  filters: null,
  bulkActions: null,
  buttons: [],
};

export {
  TableToolbar,
  FilterTypes,
};
